import React from 'react';
import { LinearProgress, Typography, Box } from '@material-ui/core';

type Props = {
  passed: number;
  total: number;
  passedWeight?: number;
  totalWeight?: number;
};

const tierFromPercentage = (percent: number): string => {
  if (percent === 100) return 'Gold';
  if (percent >= 50) return 'Silver';
  if (percent >= 1) return 'Bronze';
  return 'Beginner';
};

const formatNumber = (value: number) =>
  Number.isInteger(value) ? value.toString() : value.toFixed(1);

export const MaturityProgressBar = ({ passed, total, passedWeight, totalWeight }: Props) => {
  const hasWeights =
    typeof passedWeight === 'number' && typeof totalWeight === 'number' && totalWeight > 0;

  const effectivePassed = hasWeights ? passedWeight! : passed;
  const effectiveTotal = hasWeights ? totalWeight! : total;

  const percent = effectiveTotal > 0 ? Math.round((effectivePassed / effectiveTotal) * 100) : 0;
  const tier = tierFromPercentage(percent);

  const checkpoints = [
    { label: 'Bronze', value: 1, color: '#E76033' },
    { label: 'Silver', value: 50, color: '#5EA7F8' },
    { label: 'Gold', value: 100, color: '#F6C759' },
  ];

  return (
    <Box mb={2}>
      {/* Tier + counts */}
      <Typography variant="body1" gutterBottom>
        {tier}{' '}
        {hasWeights
          ? `(${formatNumber(effectivePassed)}/${formatNumber(effectiveTotal)} pts • ${passed}/${total} checks)`
          : `(${passed}/${total} checks)`}
      </Typography>

      {/* Progress bar with markers */}
      <Box position="relative" height={20}>
        <LinearProgress
          variant="determinate"
          value={percent}
          style={{ height: 12, borderRadius: 6 }}
        />

        {checkpoints.map(cp => (
          <Box
            key={cp.label}
            position="absolute"
            top={-2}
            left={`${cp.value}%`}
            style={{
              transform: 'translateX(-50%)',
              height: 16,
              width: 3,
              backgroundColor: cp.color,
              zIndex: 2,
              borderRadius: 1,
            }}
          />
        ))}
      </Box>

      {/* Labels for checkpoints */}
      <Box
        display="flex"
        justifyContent="space-between"
        position="relative"
        mt={0.5}
      >
        {checkpoints.map(cp => (
          <Box
            key={cp.label}
            position="absolute"
            left={`${cp.value}%`}
            style={{ transform: 'translateX(-50%)' }}
          >
            <Typography variant="caption" style={{ color: cp.color }}>
              {cp.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Progress percentage text BELOW labels */}
      <Box mt={3}>
        <Typography variant="caption">
          {percent}% towards next level
        </Typography>
      </Box>
    </Box>
  );
};
